import { t } from "../../i18n";
import type { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, RotateCcw, Save, X } from "lucide-react";
import { ConfigFields } from "../../components/tools/ToolConfigFields";
import { Toggle } from "../../components/Toggle";
import type {
  AccessRow,
  CatalogResource,
  OrganizationEntitlements,
  OrganizationToolSetting,
} from "./SiteOrganizationsPage.utils";
import { organizationResourceLabel } from "./SiteOrganizationsPage.utils";

export function SiteOrganizationAccessSection({
  activeTab,
  permissions,
  access,
  setProviderAccess,
  setModelAccess,
  setModelPolicy,
  resourceCatalog,
  entitlements,
  savingEntitlements,
  setAllToolsEnabled,
  savingCatalogResource,
  setCatalogAccess,
  toolSettings,
  openTool,
  setOpenToolId,
  setToolSettings,
  resetToolSettings,
  saveToolSettings,
}: {
  activeTab: string;
  permissions: string[];
  access: AccessRow[];
  setProviderAccess: (providerId: string, enabled: boolean) => Promise<void>;
  setModelAccess: (modelId: string, enabled: boolean) => Promise<void>;
  setModelPolicy: (
    modelId: string,
    defaultEffect: "allow" | "deny",
  ) => Promise<void>;
  resourceCatalog: CatalogResource[];
  entitlements: OrganizationEntitlements;
  savingEntitlements: boolean;
  setAllToolsEnabled: (enabled: boolean) => Promise<void>;
  savingCatalogResource: string | null;
  setCatalogAccess: (
    resource: CatalogResource,
    enabled: boolean,
  ) => Promise<void>;
  toolSettings: OrganizationToolSetting[];
  openTool: OrganizationToolSetting | null;
  setOpenToolId: (id: string | null) => void;
  setToolSettings: Dispatch<SetStateAction<OrganizationToolSetting[]>>;
  resetToolSettings: (tool: OrganizationToolSetting) => Promise<void>;
  saveToolSettings: (
    tool: OrganizationToolSetting,
    config: Record<string, unknown>,
  ) => Promise<void>;
}) {
  return (
    <>
      {activeTab === "access" &&
        permissions.includes("site:organization-access:manage") && (
          <section className="panel mb-5 flex flex-wrap items-center justify-between gap-4 p-5">
            <div>
              <h2 className="font-bold">{t("Custom models")}</h2>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "Create and manage organization-scoped routing and fallback\n                models.",
                )}
              </p>
            </div>
            <Link
              className="button"
              to={`/settings/custom-models?scope=organization&organizationId=${new URLSearchParams(window.location.search).get("organizationId") ?? ""}`}
            >
              {t("Manage custom models")}
            </Link>
          </section>
        )}
      {activeTab === "access" &&
        permissions.includes("site:organization-access:manage") && (
          <section className="panel p-5">
            <h2 className="font-bold">{t("Other platform resource access")}</h2>
            <p className="mt-1 text-sm text-stone-500">
              {t(
                "Assign site-managed tools, agents, skills, workspaces, and MCP servers to this organization.",
              )}
            </p>
            <div className="mt-4 space-y-4">
              {["tool", "agent", "skill", "workspace", "mcp_server"].map(
                (resourceType) => {
                  const items = resourceCatalog.filter(
                    (resource) => resource.resourceType === resourceType,
                  );
                  const isToolSection = resourceType === "tool";
                  return (
                    <div
                      key={resourceType}
                      className="rounded-xl border border-stone-200 p-4 dark:border-white/10 dark:bg-white/3"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="font-semibold">
                            {organizationResourceLabel(resourceType)}
                          </h3>
                          {isToolSection && (
                            <p className="mt-1 text-xs text-stone-500">
                              {t(
                                "Click a tool to configure its organization values.",
                              )}
                            </p>
                          )}
                        </div>
                        {isToolSection && (
                          <label className="flex max-w-xl items-start gap-2 text-sm">
                            <Toggle
                              className="mt-0.5"
                              checked={entitlements.allToolsEnabled}
                              disabled={savingEntitlements}
                              onChange={(event) =>
                                void setAllToolsEnabled(event.target.checked)
                              }
                            />
                            <span>
                              <strong className="block">
                                {t("Allow all platform tools")}
                              </strong>
                              <span className="mt-1 block text-xs text-stone-500">
                                {t(
                                  "Automatically enable every enabled platform tool for this organization, including tools added in the future.",
                                )}
                              </span>
                            </span>
                          </label>
                        )}
                      </div>
                      <div className="mt-3 grid gap-3 sm:grid-cols-2">
                        {items.map((resource) => {
                          const toolSetting = isToolSection
                            ? toolSettings.find(
                                (tool) => tool.id === resource.id,
                              )
                            : null;
                          const available =
                            isToolSection && entitlements.allToolsEnabled
                              ? resource.enabled
                              : resource.enabled &&
                                resource.entitlementEnabled === true;
                          if (!isToolSection)
                            return (
                              <label
                                key={resource.id}
                                className="flex items-center justify-between gap-3 text-sm"
                              >
                                <span>{resource.name}</span>
                                <input
                                  type="checkbox"
                                  checked={available}
                                  disabled={
                                    !resource.enabled ||
                                    savingCatalogResource ===
                                      `${resource.resourceType}:${resource.id}`
                                  }
                                  onChange={(event) =>
                                    void setCatalogAccess(
                                      resource,
                                      event.target.checked,
                                    )
                                  }
                                />
                              </label>
                            );
                          const open = () => setOpenToolId(resource.id);
                          return (
                            <div
                              key={resource.id}
                              role="button"
                              tabIndex={0}
                              aria-disabled={!resource.enabled}
                              onClick={open}
                              onKeyDown={(event) => {
                                if (
                                  event.key === "Enter" ||
                                  event.key === " "
                                ) {
                                  event.preventDefault();
                                  open();
                                }
                              }}
                              className={`group flex min-h-24 items-center justify-between gap-3 rounded-xl border p-4 text-left transition ${resource.enabled ? "cursor-pointer border-stone-200 hover:-translate-y-0.5 hover:border-lumen-300 hover:shadow-sm dark:border-white/10" : "cursor-not-allowed border-stone-200 opacity-50 dark:border-white/10"}`}
                            >
                              <span className="min-w-0">
                                <strong className="block truncate text-sm">
                                  {toolSetting?.displayName || resource.name}
                                </strong>
                                <span className="mt-1 block truncate font-mono text-[10px] text-stone-400">
                                  {resource.name}
                                </span>
                                <span className="mt-2 block text-xs text-stone-500">
                                  {!resource.enabled
                                    ? t("Disabled at platform level")
                                    : entitlements.allToolsEnabled
                                      ? t("Allowed by global tool access")
                                      : available
                                        ? t("Available to this organization")
                                        : t("Unavailable to this organization")}
                                </span>
                              </span>
                              <span className="flex shrink-0 items-center gap-2">
                                {!entitlements.allToolsEnabled && (
                                  <Toggle
                                    checked={available}
                                    disabled={
                                      !resource.enabled ||
                                      savingCatalogResource ===
                                        `${resource.resourceType}:${resource.id}`
                                    }
                                    aria-label={t("Enable tool")}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(event) =>
                                      void setCatalogAccess(
                                        resource,
                                        event.target.checked,
                                      )
                                    }
                                  />
                                )}
                                <ChevronRight
                                  size={17}
                                  className="text-stone-300 transition group-hover:text-lumen-600"
                                />
                              </span>
                            </div>
                          );
                        })}
                        {!items.length && (
                          <span className="text-xs text-stone-500">
                            {t("None available")}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                },
              )}
            </div>
          </section>
        )}
      {activeTab === "access" && openTool && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-stone-950/50 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpenToolId(null);
          }}
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-stone-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-stone-900"
            role="dialog"
            aria-modal="true"
            aria-labelledby="organization-tool-settings-title"
          >
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <h2 id="organization-tool-settings-title" className="font-bold">
                  {openTool.displayName || openTool.name}
                </h2>
                <p className="mt-1 font-mono text-[10px] text-stone-400">
                  {openTool.name}
                </p>
                <p className="mt-2 text-sm text-stone-500">
                  {t(
                    "Organization values override the global tool values for this organization.",
                  )}
                </p>
              </div>
              <button
                type="button"
                className="button p-1.5"
                aria-label={t("Close")}
                onClick={() => setOpenToolId(null)}
              >
                <X size={17} />
              </button>
            </div>
            <div className="mt-5">
              {Object.keys(openTool.adminSchema.properties ?? {}).length ? (
                <ConfigFields
                  schema={openTool.adminSchema}
                  value={openTool.config}
                  onChange={(config) =>
                    setToolSettings((current) =>
                      current.map((item) =>
                        item.id === openTool.id ? { ...item, config } : item,
                      ),
                    )
                  }
                />
              ) : (
                <p className="rounded-xl border border-dashed border-stone-200 p-4 text-sm text-stone-500 dark:border-white/10">
                  {t("This tool has no organization-specific settings.")}
                </p>
              )}
            </div>
            <div className="mt-6 flex flex-wrap justify-between gap-2">
              <button
                type="button"
                className="button gap-1 text-amber-700 dark:text-amber-300"
                disabled={!openTool.configured}
                onClick={() => void resetToolSettings(openTool)}
              >
                <RotateCcw size={14} /> {t("Reset to global values")}
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="button"
                  onClick={() => setOpenToolId(null)}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="button-primary gap-1"
                  onClick={() =>
                    void saveToolSettings(openTool, openTool.config)
                  }
                >
                  <Save size={14} /> {t("Save settings")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {activeTab === "access" &&
        permissions.includes("site:organization-access:manage") && (
          <section className="panel p-5">
            <h2 className="font-bold">{t("Provider and model access")}</h2>
            <p className="mt-1 text-sm text-stone-500">
              {t(
                "Grant platform and organization models from approved providers. Personal providers are owned by their members and are not managed or listed here.",
              )}
            </p>
            <div className="mt-4 space-y-4">
              {[...new Set(access.map((row) => row.providerId))].map(
                (providerId) => {
                  const providerModels = access.filter(
                    (row) => row.providerId === providerId,
                  );
                  const allEnabled = providerModels.every(
                    (row) => row.entitlementEnabled === true,
                  );
                  return (
                    <div
                      key={providerId}
                      className="rounded-xl border border-stone-200 p-4 dark:border-white/10 dark:bg-white/3"
                    >
                      <label className="flex items-center justify-between gap-3">
                        <span>
                          <strong className="block">
                            {providerModels[0].providerName}
                          </strong>
                          <span className="text-xs text-stone-500">
                            {t("models available", {
                              count: providerModels.filter(
                                (model) => model.entitlementEnabled === true,
                              ).length,
                              total: providerModels.length,
                            })}
                          </span>
                        </span>
                        <input
                          role="switch"
                          type="checkbox"
                          checked={allEnabled}
                          onChange={(event) =>
                            void setProviderAccess(
                              providerId,
                              event.target.checked,
                            )
                          }
                        />
                      </label>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {providerModels.map((model) => (
                          <label
                            key={model.modelId}
                            className="flex items-center gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              checked={model.entitlementEnabled === true}
                              onChange={(event) =>
                                void setModelAccess(
                                  model.modelId,
                                  event.target.checked,
                                )
                              }
                            />
                            <span>{model.modelName}</span>
                            <span className="ml-auto text-xs text-stone-400">
                              {model.entitlementEnabled === true
                                ? t("Available")
                                : t("Unavailable")}
                            </span>
                          </label>
                        ))}
                      </div>
                      <details className="mt-4 border-t border-stone-200 pt-3 dark:border-white/10">
                        <summary className="cursor-pointer text-xs font-semibold text-stone-600 dark:text-stone-300">
                          {t("Advanced access policies")}
                        </summary>
                        <p className="mt-2 text-xs text-stone-500">
                          {t(
                            "Choose the default policy for members when you use more granular access rules. This does not enable a model; use the availability controls above for that.",
                          )}
                        </p>
                        <div className="mt-3 space-y-2">
                          {providerModels.map((model) => (
                            <label
                              key={model.modelId}
                              className="flex items-center justify-between gap-3 text-sm"
                            >
                              <span>{model.modelName}</span>
                              <select
                                className="field w-auto py-1"
                                value={model.defaultEffect ?? "allow"}
                                disabled={model.entitlementEnabled !== true}
                                onChange={(event) =>
                                  void setModelPolicy(
                                    model.modelId,
                                    event.target.value as "allow" | "deny",
                                  )
                                }
                              >
                                <option value="allow">
                                  {t("Allow by default")}
                                </option>
                                <option value="deny">
                                  {t("Deny by default")}
                                </option>
                              </select>
                            </label>
                          ))}
                        </div>
                      </details>
                    </div>
                  );
                },
              )}
            </div>
          </section>
        )}
    </>
  );
}
